import type { ElementType, ReactNode } from "react";

/**
 * Typography variants — sourced from Figma design system (node 52:980).
 * Each variant binds size + weight + line-height + letter-spacing in one token.
 */
export type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "bodyXL"
  | "bodyLg"
  | "bodyLgBold"
  | "body"
  | "bodyBold"
  | "bodySm"
  | "bodySmMedium"
  | "bodySmBold"
  | "bodyXs"
  | "bodyXsBold"
  | "capsBig"
  | "caps"
  | "capsMedium"
  | "capsSm"
  | "button";

export type TypographyTone = "dark" | "body" | "muted" | "accent" | "inherit" | "amber" | "greenDark" | "green" | "error";

export interface TypographyBaseProps {
  variant: TypographyVariant;
  tone?: TypographyTone;
  as?: ElementType;
  className?: string;
  children: ReactNode;
  dir?: "ltr" | "rtl" | "auto";
  lang?: string;
  "data-testid"?: string;
}

export const TONE_CLASSES: Record<TypographyTone, string> = {
  dark: "text-textDark",
  body: "text-textBody",
  muted: "text-textMuted",
  accent: "text-accentCyan",
  inherit: "text-inherit",
  amber: "text-accentAmber",
  greenDark: "text-accentGreenDark",
  green: "text-accentGreen",
  /** Validation and error messages. */
  error: "text-accentError",
};

/**
 * Maps variant → Tailwind size token (defined in tailwind.config.js fontSize).
 * The size token already encodes font-size, line-height, letter-spacing, and weight.
 */
export const VARIANT_SIZE_CLASS: Record<TypographyVariant, string> = {
  h1: "text-h1",
  h2: "text-h2",
  h3: "text-h3",
  bodyXL: "text-bodyXL",
  bodyLg: "text-bodyLg",
  bodyLgBold: "text-bodyLgBold",
  body: "text-body",
  bodyBold: "text-bodyBold",
  bodySm: "text-bodySm",
  bodySmMedium: "text-bodySmMedium",
  bodySmBold: "text-bodySmBold",
  bodyXs: "text-bodyXs",
  bodyXsBold: "text-bodyXsBold",
  capsBig: "text-capsBig font-mono uppercase",
  caps: "text-caps uppercase",
  capsMedium: "text-capsMedium uppercase",
  capsSm: "text-capsSm uppercase",
  button: "text-button",
};

/** Variants that semantically represent headings. */
export const HEADING_VARIANTS = new Set<TypographyVariant>(["h1", "h2", "h3"]);

/** Default HTML element per variant when `as` is not provided. */
export const DEFAULT_ELEMENT: Record<TypographyVariant, ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  bodyXL: "p",
  bodyLg: "p",
  bodyLgBold: "p",
  body: "p",
  bodyBold: "p",
  bodySm: "p",
  bodySmMedium: "p",
  bodySmBold: "p",
  bodyXs: "p",
  bodyXsBold: "p",
  capsBig: "span",
  caps: "span",
  capsMedium: "span",
  capsSm: "span",
  button: "span",
};

/** Default tone per variant — matches Figma sample colors. */
export const DEFAULT_TONE: Record<TypographyVariant, TypographyTone> = {
  h1: "dark",
  h2: "dark",
  h3: "dark",
  bodyXL: "body",
  bodyLg: "body",
  bodyLgBold: "body",
  body: "body",
  bodyBold: "body",
  bodySm: "body",
  bodySmMedium: "body",
  bodySmBold: "body",
  bodyXs: "body",
  bodyXsBold: "body",
  capsBig: "body",
  caps: "accent",
  capsMedium: "accent",
  capsSm: "accent",
  button: "body",
};
